/**
 * Persistent red banner shown on every page while impersonation is active.
 * D-074 (2026-05-22)
 */
import { useEffect, useState } from "react";
import {
  getImpersonationTarget,
  subscribe,
  clearImpersonationTarget,
  clearScopedSession,
  type ImpersonationTarget,
} from "@/lib/impersonation";
import { api } from "@/lib/session";
import { queryClient } from "@/App";

function useImpersonationTarget(): ImpersonationTarget | null {
  const [t, setT] = useState<ImpersonationTarget | null>(getImpersonationTarget());
  useEffect(() => {
    return subscribe(() => setT(getImpersonationTarget()));
  }, []);
  return t;
}

function formatExpiry(iso: string | null): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const mins = Math.round(ms / 60_000);
  return mins <= 1 ? "less than 1 min" : `${mins} min`;
}

export function ImpersonationBanner() {
  const target = useImpersonationTarget();
  const [, setTick] = useState(0);

  // Re-render every 30s so the expiry countdown stays fresh.
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [target]);

  async function handleExit() {
    try {
      await api("/api/admin/impersonate/stop", { method: "POST" });
    } catch {
      // Even if the stop call fails, clear local state so the user can recover.
    }
    clearImpersonationTarget();
    // Wave I (B-BDR6): wipe React Query cache and role-scoped localStorage so
    // the actor does not see the impersonated user's stale data after exit.
    queryClient.clear();
    clearScopedSession();
    // Hard reload so every component re-fetches fresh data as the real actor.
    window.location.reload();
  }

  if (!target) return null;

  return (
    <div
      role="alert"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 9999,
        width: "100%",
        background: "#7F1D1D",
        color: "#FFFFFF",
        borderBottom: "1px solid #450A0A",
      }}
    >
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "8px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <span
            style={{
              background: "#FFFFFF",
              color: "#7F1D1D",
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Impersonating
          </span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Viewing as <strong>{target.name}</strong>
            <span style={{ opacity: 0.85 }}> ({target.role}) — session ends in {formatExpiry(target.expiresAt)}</span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleExit}
          style={{
            background: "#FFFFFF",
            color: "#7F1D1D",
            border: "none",
            padding: "6px 14px",
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Exit impersonation
        </button>
      </div>
    </div>
  );
}
